import {DispatchStation} from "../components/common/types";
import {createContext, useContext, useReducer} from "react";
import log from "loglevel";

type DispatchStationProviderProps = {
  children: JSX.Element[] | JSX.Element;
};

type DispatchStationState = {
  isDispatchStationLoaded?: boolean;
  defaultDispatchStation: DispatchStation[] | undefined;
  selectedDispatchStations: Map<string, DispatchStation>;
};

export enum DispatchStationActionTypes {
  SET_IS_DISPATCH_STATION_LOADED = "SET_IS_DISPATCH_STATION_LOADED",
  ADD_SELECTED_DISPATCH_STATION = "ADD_SELECTED_DISPATCH_STATION",
  REMOVE_SELECTED_DISPATCH_STATION = "REMOVE_SELECTED_DISPATCH_STATION",
  CLEAR_SELECTED_DISPATCH_STATIONS = "CLEAR_SELECTED_DISPATCH_STATIONS",
  INITIAL_SELECTED_DISPATCH_STATIONS = "INITIAL_SELECTED_DISPATCH_STATIONS",
  SET_DEFAULT_DISPATCH_STATIONS = "SET_DEFAULT_DISPATCH_STATIONS",
  CLEAR_DEFAULT_DISPATCH_STATION = "CLEAR_DEFAULT_DISPATCH_STATION"
}

export type DispatchStationAction =
  | {type: DispatchStationActionTypes.SET_IS_DISPATCH_STATION_LOADED; payload: boolean}
  | {type: DispatchStationActionTypes.ADD_SELECTED_DISPATCH_STATION; payload: DispatchStation}
  | {type: DispatchStationActionTypes.REMOVE_SELECTED_DISPATCH_STATION; payload: DispatchStation}
  | {type: DispatchStationActionTypes.CLEAR_SELECTED_DISPATCH_STATIONS}
  | {type: DispatchStationActionTypes.INITIAL_SELECTED_DISPATCH_STATIONS; payload: DispatchStation[]}
  | {type: DispatchStationActionTypes.SET_DEFAULT_DISPATCH_STATIONS; payload: DispatchStation[]}
  | {type: DispatchStationActionTypes.CLEAR_DEFAULT_DISPATCH_STATION};

export const getSelectedDispatchStations = (state: DispatchStationState): Map<string, DispatchStation> => {
  if (state.selectedDispatchStations.size > 0) {
    log.debug("Getting selected dispatch stations: ", Array.from(state.selectedDispatchStations.values()));
    return state.selectedDispatchStations;
  } else if (state.defaultDispatchStation) {
    log.debug("Getting default dispatch station as selected dispatch station: ", state.defaultDispatchStation);
    const defaultMap = new Map<string, DispatchStation>();
    state.defaultDispatchStation.forEach((station) => {
      defaultMap.set(station.stationId, station);
    });
    return defaultMap;
  }
  return new Map<string, DispatchStation>();
};

export const hasSelectedDispatchStations = (state: DispatchStationState): boolean => {
  return getSelectedDispatchStations(state).size > 0;
};

const reducer = (state: DispatchStationState, action: DispatchStationAction): DispatchStationState => {
  switch (action.type) {
    case DispatchStationActionTypes.SET_IS_DISPATCH_STATION_LOADED:
      log.debug("Setting isDispatchStationLoaded to: ", action.payload);
      return {
        ...state,
        isDispatchStationLoaded: action.payload
      };

    case DispatchStationActionTypes.ADD_SELECTED_DISPATCH_STATION: {
      log.debug("Adding to selected dispatch stations: ", action.payload);
      if (!action.payload) {
        log.warn("Attempted to add undefined dispatch station to selected dispatch stations");
        return state;
      }
      const newSelectedStations = new Map(state.selectedDispatchStations);
      newSelectedStations.set(action.payload.stationId, action.payload);
      return {
        ...state,
        selectedDispatchStations: newSelectedStations
      };
    }

    case DispatchStationActionTypes.REMOVE_SELECTED_DISPATCH_STATION: {
      log.debug("Removing from selected dispatch stations: ", action.payload);
      if (!action.payload) {
        log.warn("Attempted to remove undefined dispatch station from selected dispatch stations");
        return state;
      }
      const newSelectedStations = new Map(state.selectedDispatchStations);
      newSelectedStations.delete(action.payload.stationId);
      return {
        ...state,
        selectedDispatchStations: newSelectedStations
      };
    }

    case DispatchStationActionTypes.CLEAR_SELECTED_DISPATCH_STATIONS:
      log.debug("Clearing selected dispatch stations");
      return {
        ...state,
        selectedDispatchStations: new Map<string, DispatchStation>()
      };

    case DispatchStationActionTypes.INITIAL_SELECTED_DISPATCH_STATIONS: {
      log.debug("Initializing selected dispatch stations: ", action.payload);
      const newSelectedStations = new Map<string, DispatchStation>();
      action.payload.forEach((dispatchStation) => {
        newSelectedStations.set(dispatchStation.stationId, dispatchStation);
      });
      return {
        ...state,
        selectedDispatchStations: newSelectedStations
      };
    }

    case DispatchStationActionTypes.SET_DEFAULT_DISPATCH_STATIONS:
      return {
        ...state,
        defaultDispatchStation: action.payload
      };

    case DispatchStationActionTypes.CLEAR_DEFAULT_DISPATCH_STATION:
      if (!state.defaultDispatchStation) return state;
      return {
        ...state,
        defaultDispatchStation: undefined
      };

    default:
      return state;
  }
};

const initialState: DispatchStationState = {
  isDispatchStationLoaded: false,
  defaultDispatchStation: undefined,
  selectedDispatchStations: new Map<string, DispatchStation>()
};

const DispatchStationContext = createContext<DispatchStationState>(initialState);
const DispatchStationDispatchContext = createContext<React.Dispatch<DispatchStationAction> | undefined>(undefined);

const DispatchStationProvider = ({children}: DispatchStationProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <DispatchStationContext.Provider value={state}>
      <DispatchStationDispatchContext.Provider value={dispatch}>{children}</DispatchStationDispatchContext.Provider>
    </DispatchStationContext.Provider>
  );
};

const useDispatchStationContext = () => {
  const context = useContext(DispatchStationContext);
  if (context === undefined) {
    throw new Error("useDispatchStationContext must be used within a DispatchStationProvider");
  }
  return context;
};

const useDispatchStationDispatch = () => {
  const context = useContext(DispatchStationDispatchContext);
  if (context === undefined) {
    throw new Error("useDispatchStationDispatch must be used within a DispatchStationDataProvider");
  }
  return context;
};

export default DispatchStationProvider;
export {DispatchStationContext, DispatchStationDispatchContext, useDispatchStationContext, useDispatchStationDispatch};
export type {DispatchStationState};
